import * as z from 'zod'

import { v2Text, v2TextWithPetName } from '@flows/configs/petHealthV2/steps/elements/shared'
import { DEDUCTIBLE_HEADER } from '@flows/configs/shared/constants'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import type { StepConfigParams } from '@flows/configs/types'
import type { AnyCustomComponent } from '@flows/core/types/CustomComponent'
import type { TransformedOption } from '@flows/core/types/stepContentTypes'
import type { AnyStepConfiguration } from '@flows/core/types/stepTypes'
import { BaseSchema_PetHealthV2GetsafeDe } from '@flows/insurance/schemas'
import Schema_Quote from '@flows/insurance/schemas/shared/Schema_Quote'

const { shape } = BaseSchema_PetHealthV2GetsafeDe
const DEDUCTIBLE_OPTIONS = schemaUtils.getEnumValuesFromPipelineSchema(shape.deductible_percentage)
const INSURED_SUM_OPTIONS = shape.sum_insured.options

export const QUOTE_PARAMS = [
  'tariff_type',
  'deductible_percentage',
  'sum_insured',
  'addon_dental',
  'addon_sos_budget',
  'addon_preventive_care',
] as const

const insuredSumLabel = (params: StepConfigParams, value: (typeof INSURED_SUM_OPTIONS)[number]) =>
  value === 'unlimited'
    ? v2Text('steps.pet_quote.insured_sum.unlimited')
    : ({
        type: 'nonTranslatableText',
        text: params.clientHelpers.formatter.formatCurrency(Number(value)),
      } as const)

const pet_quote = (params: StepConfigParams) => {
  const { getParamPath } = params.helperFunctions
  const quoteStep = {
    type: 'customComponent',
    name: 'QuoteStep',
    props: {
      product: params.productKey,
      headerTitle: v2TextWithPetName(params, 'steps.pet_quote.title'),
      configurableParameters: [
        DEDUCTIBLE_HEADER({
          title: 'sharedFlows_common:steps.deductibles.percent',
          modalTitle: 'sharedFlows_common:generic.deductible.percent.title',
          modalInfo: 'sharedFlows_petHealthV2:steps.pet_quote.deductible.info',
        }),
        {
          type: 'dropDown',
          label: v2Text('steps.pet_quote.deductible.label'),
          useFilterInput: false,
          styleType: 'outlined',
          name: getParamPath('deductible_percentage'),
          options: DEDUCTIBLE_OPTIONS.map(
            (option): TransformedOption => ({
              label: {
                type: 'nonTranslatableText',
                text: params.clientHelpers.formatter.formatPercent(Number(option)),
              },
              value: Number(option),
            })
          ),
        },
        {
          type: 'harmonyTitle',
          text: v2Text('steps.pet_quote.insured_sum.title'),
          infoButton: {
            modalConfiguration: [
              { type: 'title', text: v2Text('steps.pet_quote.insured_sum.title') },
              { type: 'text', text: v2Text('steps.pet_quote.insured_sum.info') },
            ],
          },
        },
        {
          type: 'dropDown',
          label: v2Text('steps.pet_quote.insured_sum.label'),
          useFilterInput: false,
          styleType: 'outlined',
          name: getParamPath('sum_insured'),
          options: INSURED_SUM_OPTIONS.map((value) => ({
            label: insuredSumLabel(params, value),
            value,
          })),
        },
      ],
    },
    inputs: [],
    outputs: [],
  } as const satisfies AnyCustomComponent

  return {
    stepContent: [quoteStep],
    validationStrategy: 'onChange',
    showOrderSummary: true,
    nonStickyFooter: true,
    schema: Schema_Quote.merge(
      schemaUtils.generateZodObjectFromPaths({
        ...Object.fromEntries(QUOTE_PARAMS.map((param) => [getParamPath(param), shape[param]])),
        product_keys: z.object({ [params.productKey]: z.boolean().optional() }).optional(),
      })
    ),
    onPrepare: {
      type: 'dispatchedHook',
      callbackConfig: {
        name: 'prepareQuoteStep',
        options: { productKey: params.productKey },
        inputs: [],
        outputs: [],
      },
    },
    subFlow: params.subFlow,
    nextStep: 'pet_health_v2_personal_details',
  } as const satisfies AnyStepConfiguration
}

export default pet_quote
